import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { servicesData } from "./Servicesdata";
import { useNavigate, useParams } from "react-router-dom";
import DemoBookingAssistant from "../Chatbot/Demobookingassistant";

// ─── Color maps per service ───────────────────────────────────────────────────
const colorMap = {
  blue:   { bg: "bg-orange-500",   light: "bg-orange-50",   text: "text-orange-500",   border: "border-orange-200",  badge: "bg-orange-100 text-orange-700"  },
  indigo: { bg: "bg-indigo-600", light: "bg-indigo-50", text: "text-indigo-600", border: "border-indigo-200", badge: "bg-indigo-100 text-indigo-700" },
  sky:    { bg: "bg-sky-500",    light: "bg-sky-50",    text: "text-sky-500",    border: "border-sky-200",    badge: "bg-sky-100 text-sky-700"    },
  violet: { bg: "bg-violet-600", light: "bg-violet-50", text: "text-violet-600", border: "border-violet-200", badge: "bg-violet-100 text-violet-700" },
  emerald:{ bg: "bg-emerald-500",light: "bg-emerald-50",text: "text-emerald-600",border: "border-emerald-200",badge: "bg-emerald-100 text-emerald-700" },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CheckIcon = ({ color }) => (
  <svg viewBox="0 0 20 20" fill="none" className={`w-5 h-5 shrink-0 mt-0.5 ${color}`}>
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <path d="M6.5 10.5l2.5 2.5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function FAQItem({ q, a, isOpen, onToggle, colorText }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button onClick={onToggle} className="w-full flex items-start gap-4 py-5 text-left group">
        <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-200 ${isOpen ? "bg-orange-500" : "bg-gray-100 group-hover:bg-gray-200"}`}>
          <svg viewBox="0 0 20 20" fill="none" className="w-3.5 h-3.5">
            {isOpen
              ? <rect x="4" y="9.2" width="12" height="2" rx="1" fill={isOpen ? "white" : "#374151"} />
              : <><rect x="4" y="9.2" width="12" height="2" rx="1" fill="#374151" /><rect x="9.2" y="4" width="2" height="12" rx="1" fill="#374151" /></>
            }
          </svg>
        </span>
        <span className={`font-bold text-[0.97rem] transition-colors duration-200 ${isOpen ? colorText : "text-gray-900"}`}>{q}</span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="ans"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 pl-11 text-gray-500 text-[0.91rem] leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
 const ServiceDetailPage = ({ serviceId = "" })=> {
  const {id} = useParams();
  const [activeService, setActiveService] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const navigate  = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);

  const service = servicesData.find((s) => s.id === activeService) || servicesData[0];
  const c = colorMap[service.color] || colorMap.blue;

  useEffect(()=>{
    setActiveService(id);
  },[id]);

  return (
    <div className="min-h-screen  font-sans">
      <div className="h-[90px] bg-white/90" />
      {/* ── Service Sidebar Nav (sticky left on desktop, top tabs on mobile) ── */}
      <div className="flex flex-col lg:flex-row">

        {/* Sidebar */}
        <aside className="lg:max-w-md lg:min-h-screen bg-gray-100 lg:sticky lg:top-0 lg:h-screen  overflow-y-auto shrink-0">
          
          <div className="p-6 border-b border-white/10">
            <h2 className="text-gray-900 font-extrabold text-lg">Our Services</h2>
            <p className="text-gray-800 text-xs mt-1">Select a service below</p>
          </div>
          <nav className="p-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible">
            {servicesData.map((s) => {
              const sc = colorMap[s.color] || colorMap.blue;
              const isActive = s.id === activeService;
              return (
                <motion.button
                  key={s.id}
                  onClick={() => { 
                    setActiveService(s.id); 
                    setOpenFaq(0);
                    window.scrollTo({
                      top:0,
                      behavior: "smooth"
                    })
                  }}
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-left w-full shrink-0 lg:shrink transition-all duration-200
                    ${isActive ? `${sc.bg} text-gray-50 shadow-lg` : "text-gray-400 hover:text-gray-950 hover:bg-white/5"}`}
                >
                  <span className="text-xl">{s.icon}</span>
                  <span className="font-semibold text-[0.9rem] whitespace-nowrap">{s.title}</span>
                </motion.button>
              );
            })}
          </nav>
        </aside>

        {/* ── Main Content ── */}
        <AnimatePresence mode="wait">
          <motion.main
            key={activeService}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex-1 overflow-x-hidden"
          >

            {/* Hero */}
            <div className="relative h-72 sm:h-96 overflow-hidden">
              <img src={service.heroImage} alt={service.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2e]/90 via-[#0d1b2e]/60 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-12">
                <motion.span
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={`inline-block self-start px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-3 ${c.badge}`}
                >
                  {service.title}
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="text-white font-extrabold text-2xl sm:text-4xl leading-tight max-w-2xl"
                >
                  {service.tagline}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.26 }}
                  className="text-gray-300 text-sm sm:text-base mt-3 max-w-xl leading-relaxed"
                >
                  {service.shortDesc}
                </motion.p>
              </div>
            </div>

            <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 space-y-16 mb-10">

              {/* Stats */}
              <motion.div
                className="grid grid-cols-2 sm:grid-cols-4 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {service.stats.map((stat, i) => (
                  <div key={i} className={`${c.light} rounded-2xl p-5 text-center border ${c.border}`}>
                    <div className={`text-2xl sm:text-3xl font-extrabold ${c.text}`}>{stat.value}</div>
                    <div className="text-gray-600 text-xs sm:text-sm mt-1 font-medium">{stat.label}</div>
                  </div>
                ))}
              </motion.div>

              {/* Overview */}
              <section>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-5">{service.overview.heading}</h2>
                {service.overview.body.split("\n\n").map((para, i) => (
                  <p key={i} className="text-gray-600 text-[0.95rem] leading-relaxed mb-4">{para}</p>
                ))}
              </section>

              {/* Features */}
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <div className={`w-1 h-8 rounded-full ${c.bg}`} />
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Key Capabilities</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {service.features.map((f, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.08 }}
                      whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}
                      className={`bg-white border ${c.border} rounded-2xl p-6 shadow-sm cursor-default`}
                    >
                      <div className={`w-10 h-10 rounded-xl ${c.light} flex items-center justify-center mb-4`}>
                        <div className={`w-3 h-3 rounded-full ${c.bg}`} />
                      </div>
                      <h3 className="font-bold text-gray-900 text-[1rem] mb-2">{f.title}</h3>
                      <p className="text-gray-500 text-[0.88rem] leading-relaxed">{f.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </section>

              {/* Process */}
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <div className={`w-1 h-8 rounded-full ${c.bg}`} />
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Our Delivery Process</h2>
                </div>
                <div className="relative">
                  {/* Connector line */}
                  <div className={`hidden sm:block absolute left-6 top-8 bottom-8 w-0.5 ${c.bg} opacity-20`} />
                  <div className="space-y-5">
                    {service.process.map((step, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.45, delay: i * 0.09 }}
                        className="flex gap-5 items-start"
                      >
                        <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center shrink-0 text-white font-extrabold text-sm shadow-md`}>
                          {step.step}
                        </div>
                        <div className={`flex-1 bg-gray-50 rounded-xl p-4 border ${c.border}`}>
                          <h4 className="font-bold text-gray-900 text-[0.97rem] mb-1">{step.title}</h4>
                          <p className="text-gray-500 text-[0.87rem] leading-relaxed">{step.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Benefits */}
              <section className={`${c.light} rounded-3xl p-8 border ${c.border}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-1 h-8 rounded-full ${c.bg}`} />
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Business Benefits</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.benefits.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06 }}
                      className="flex items-start gap-3"
                    >
                      <CheckIcon color={c.text} />
                      <span className="text-gray-700 text-[0.9rem] leading-relaxed">{b}</span>
                    </motion.div>
                  ))}
                </div>
              </section>

              {/* Industries + Technologies */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <section>
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-1 h-6 rounded-full ${c.bg}`} />
                    <h2 className="text-xl font-extrabold text-gray-900">Industries We Serve</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {service.industries.map((ind, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold ${c.badge} border ${c.border}`}
                      >
                        {ind}
                      </motion.span>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-1 h-6 rounded-full ${c.bg}`} />
                    <h2 className="text-xl font-extrabold text-gray-900">Technologies & Tools</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {service.technologies.map((tech, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-900 text-white"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </section>
              </div>

              {/* FAQ */}
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <div className={`w-1 h-8 rounded-full ${c.bg}`} />
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Frequently Asked Questions</h2>
                </div>
                <div className="bg-gray-50 rounded-2xl px-6 py-2 border border-gray-100">
                  {service.faq.map((item, i) => (
                    <FAQItem
                      key={i}
                      q={item.q}
                      a={item.a}
                      isOpen={openFaq === i}
                      onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                      colorText={c.text}
                    />
                  ))}
                </div>
              </section>

              {/* CTA */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`${c.bg}  rounded-3xl p-10 text-center text-white`}
              >
                <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Ready to Get Started?</h2>
                <p className="text-white/80 text-[0.95rem] mb-7 max-w-lg mx-auto">
                  Talk to one of our {service.title} experts today and discover how we can deliver measurable results for your business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.a
                   onClick={()=>{ 
                    setModalOpen(true);
                        // window.scrollTo(0,0);
                   }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="px-8 py-3.5 bg-white text-gray-900 font-bold rounded-full text-[0.95rem] cursor-pointer"
                  >
                    Book a Free Consultation
                  </motion.a>
                  {/* <motion.a
                    href="#"
                    whileHover={{ scale: 1.04 }}
                    className="px-8 py-3.5 border-2 border-white text-white font-bold rounded-full text-[0.95rem] cursor-pointer"
                  >
                    Download Brochure
                  </motion.a> */}
                </div>
              </motion.section>

            </div>
          </motion.main>
        </AnimatePresence>
      </div>

       <DemoBookingAssistant
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            autoStart={true}
          />
    </div>
  );
}


export default ServiceDetailPage;